/**
 * Firebase Cloud Messaging (FCM) for push notifications.
 * - Web: uses FCM web (service worker)
 * - Capacitor (iOS/Android): uses @capacitor-firebase/messaging for native push
 */
import { Capacitor } from "@capacitor/core";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { app } from "../config/firebase";
import { createLogger } from "./logger";

const logger = createLogger("FirebaseMessaging");

let foregroundHandlerSetup = false;

function setupForegroundHandler(messaging) {
  if (foregroundHandlerSetup) return;
  foregroundHandlerSetup = true;
  try {
    onMessage(messaging, (payload) => {
      console.log("[FCM] Foreground message received:", payload);
      const title = payload.notification?.title || payload.data?.title || "Pani";
      const body = payload.notification?.body || payload.data?.body || "";
      // Always dispatch for in-app toast (works even when browser Notification is blocked)
      window.dispatchEvent(
        new CustomEvent("pani-fcm-message", { detail: { title, body, payload } })
      );
      // Also try browser Notification when permitted
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body, icon: "/logo.png" });
      }
    });
    console.log("[FCM] Foreground message handler registered");
  } catch (err) {
    console.error("[FCM] Foreground handler failed:", err);
  }
}

/**
 * Test if browser notifications work. Call from console: window.__testPaniNotification()
 */
export function testLocalNotification() {
  if (!("Notification" in window)) {
    console.error("[FCM] Notification API not supported");
    return;
  }
  if (Notification.permission !== "granted") {
    console.warn("[FCM] Permission not granted:", Notification.permission);
    Notification.requestPermission().then((p) => {
      console.log("[FCM] Permission result:", p);
      if (p === "granted") new Notification("Pani Test", { body: "Local test works!" });
    });
    return;
  }
  new Notification("Pani Test", { body: "If you see this, browser notifications work!" });
  console.log("[FCM] Test notification shown");
}

/**
 * Set up foreground message listener (called from App on mount).
 * Also called after getToken so handler is ready when token is obtained.
 */
export async function setupForegroundMessageHandler() {
  if (Capacitor.isNativePlatform()) return;
  const supported = await isSupported();
  if (!supported) {
    console.warn("[FCM] Not supported - skipping foreground handler");
    return;
  }
  try {
    const messaging = getMessaging(app);
    setupForegroundHandler(messaging);
  } catch (err) {
    console.error("[FCM] setupForegroundMessageHandler failed:", err);
  }
}

/**
 * Get FCM token. Returns null if unsupported, permission denied, or config missing.
 * Uses native plugin on Capacitor (iOS/Android), FCM web otherwise.
 * On iOS simulator, native APNs is unavailable - falls back to web FCM for token.
 */
export async function getFCMToken() {
  try {
    if (Capacitor.isNativePlatform()) {
      const nativeToken = await getFCMTokenNative();
      if (nativeToken) return nativeToken;
      // iOS simulator: native APNs unavailable, fall back to web FCM for token
      logger.log("Native token failed, falling back to web FCM (e.g. iOS simulator)");
      return getFCMTokenWeb();
    }
    return getFCMTokenWeb();
  } catch (err) {
    logger.error("Failed to get FCM token:", err);
    return null;
  }
}

/**
 * Native (Capacitor iOS/Android) - uses @capacitor-firebase/messaging
 * On iOS, APNs token can take a moment (especially on simulator) - retry with delays like pani-delivery
 */
async function getFCMTokenNative() {
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive !== "granted") {
      const { receive: newReceive } = await FirebaseMessaging.requestPermissions();
      if (newReceive !== "granted") {
        logger.log("Push permission denied on native");
        return null;
      }
    }

    const isIOS = Capacitor.getPlatform() === "ios";
    const maxAttempts = isIOS ? 20 : 3;
    const delayMs = (attempt) => Math.min(250 * (attempt + 1), 1000);

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { token } = await FirebaseMessaging.getToken();
        if (token) {
          logger.log("FCM token obtained (native)");
          return token;
        }
      } catch (err) {
        if (i === maxAttempts - 1) {
          logger.error("Native FCM token failed:", err);
          throw err;
        }
        logger.log(`FCM token attempt ${i + 1}/${maxAttempts} failed, retrying...`);
      }
      if (i < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs(i)));
      }
    }
    return null;
  } catch (err) {
    logger.error("Native FCM token failed:", err);
    return null;
  }
}

/**
 * Web - uses Firebase Messaging (service worker)
 */
async function getFCMTokenWeb() {
  try {
    const supported = await isSupported();
    if (!supported) {
      logger.warn(
        "FCM not supported - use HTTPS or localhost, and a supported browser (Chrome, Firefox, Edge)"
      );
      return null;
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      logger.warn("VITE_FIREBASE_VAPID_KEY not set - skipping FCM token");
      return null;
    }

    // Explicitly register service worker first (helps with some hosting setups)
    let registration;
    if ("serviceWorker" in navigator) {
      registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
        scope: "/"
      });
      await navigator.serviceWorker.ready;
    }

    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: vapidKey.trim(),
      serviceWorkerRegistration: registration || undefined
    });

    if (token) {
      console.log("[FCM] Token obtained (web), length:", token.length);
      setupForegroundHandler(messaging);
      return token;
    }

    logger.log("No FCM token returned (user may have denied permission)");
    return null;
  } catch (err) {
    logger.error("FCM web token failed:", err?.code || err?.message, err);
    if (err?.code === "messaging/failed-service-worker-registration") {
      console.warn(
        "[FCM] Service worker failed. Check: 1) /firebase-messaging-sw.js is accessible, 2) Enable FCM Registration API: https://console.cloud.google.com/apis/library/fcmregistrations.googleapis.com"
      );
    }
    return null;
  }
}
